import { useState, useEffect } from "react";
import { Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { CustomSelect } from "../pages/home/Home";
import { supabase } from "../supabaseClient";
import Icon from "../components/Icon";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";
import ErrorSvg from "../assets/image svg/error.svg";

const PasswordReset = ({ setLanguage }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user has valid reset session
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // If no session, redirect to forgot password page
        navigate("/password-forgot");
      }
    };

    checkSession();
  }, [navigate]);

  const validatePassword = (pwd) => {
    if (pwd.length < 6) {
      return "Password must be at least 6 characters long";
    }
    return null;
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset states
    setError("");
    setSuccess(false);

    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        console.error("Password update error:", error);
        setError(error.message || "Failed to update password. Please try again.");
      } else {
        setSuccess(true);
        // Redirect after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="login">
        <div className="z-[1000000] relative flex justify-center items-center pt-12 w-full">
          <div className="flex justify-between items-center w-[90%]">
            <Link to="/">
              <Icon
                icon="heroicons:arrow-left"
                className="w-12 h-12 text-white"
                alt="Back"
              />
            </Link>
            <Link to="/">
              <Logo />
            </Link>
            <CustomSelect
              options={[
                { value: "nl", label: "🇳🇱 Nederlands" },
                { value: "en", label: "🇺🇸 English" },
              ]}
              onSelect={(value) => {
                setLanguage(value);
              }}
            />
          </div>
        </div>

        <div className="max-lg:!items-start max-lg:mt-12 login_form">
          <form
            className="w-full form"
            name="f1"
            onSubmit={handlePasswordReset}
            method="post"
          >
            <div className="lg">
              <div className="">
                <p className="flex protected">
                  <span className="ml-1.5 text-[24px]">
                    Enter a new password
                  </span>
                </p>
                <div>
                  <input
                    type="password"
                    name="password"
                    className={
                      "w-full rounded-[6px] !py-[15px] outline-none !text-white !border-none !bg-[#130826]"
                    }
                    id="newPassword"
                    placeholder="New password (min. 6 characters)"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(""); // Clear error when user types
                    }}
                    disabled={loading || success}
                    minLength={6}
                  />
                </div>
                <div>
                  <input
                    type="password"
                    name="confirmPassword"
                    className={
                      "w-full rounded-[6px] mt-3 !py-[15px] outline-none !text-white !border-none !bg-[#130826]"
                    }
                    id="confirmPassword"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError(""); // Clear error when user types
                    }}
                    disabled={loading || success}
                  />
                </div>

                <div>
                  {success && (
                    <div className="flex items-center mb-3 !font-semibold !text-green-500">
                      <span className="ml-1">
                        Password updated successfully! Redirecting you to the home page...
                      </span>
                    </div>
                  )}

                  {error && (
                    <div
                      id="errorlocation"
                      className="flex items-center mb-3 !font-semibold !text-red-500"
                    >
                      <img src={ErrorSvg} alt="error" className="mr-2 danger" />
                      <span className="ml-1">
                        {error}
                      </span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-[#6A1BD1] to-[#7721E5] shadow-md mt-5 px-[20px] py-[14px] rounded-[8px] w-full font-semibold text-[16px] text-white btn"
                    disabled={loading || success}
                  >
                    {loading ? "Updating..." : success ? "Password Updated!" : t("continue")}
                  </Button>

                  {!success && (
                    <div className="mt-4 text-center">
                      <Link
                        to="/password-forgot"
                        className="text-[#B9B6BF] text-[14px] hover:text-white transition-colors"
                      >
                        Need a new reset link?
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </header>
    </>
  );
};

export default PasswordReset;
